// Presentation-only clock-time formatter (shared).
//
// This does NOT change any timezone math — it formats the SAME instant the
// caller already has. By default it reads the browser-local clock (the tz all
// the client-facing session/slot displays already use); pass an IANA `timeZone`
// to format the same instant in an explicit zone instead.
//
// Display rule (AM unchanged; PM trimmed):
//   AM → "hh:mm AM" — zero-padded hour, exactly as today (e.g. "09:00 AM").
//   PM → drop the leading zero on the hour AND drop ":00" on the hour:
//        "03:00 PM" → "3 PM"
//        "04:00 PM" → "4 PM"
//        "12:00 PM" → "12 PM"   (noon)
//        "05:30 PM" → "5:30 PM"
//        "12:15 PM" → "12:15 PM"
//   (Midnight 12:00 AM stays AM → "12:00 AM", unchanged.)
//
// Returns '' if the inputs are unusable, so callers degrade gracefully.
export function formatClockTime(date, timeZone) {
  let h, m;
  if (timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, hour: 'numeric', minute: '2-digit', hour12: false,
    }).formatToParts(date);
    h = Number(parts.find((p) => p.type === 'hour')?.value);
    m = Number(parts.find((p) => p.type === 'minute')?.value);
    if (h === 24) h = 0; // some engines render midnight as 24 with hour12:false
  } else {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    h = date.getHours();
    m = date.getMinutes();
  }
  if (Number.isNaN(h) || Number.isNaN(m)) return '';

  const isPM = h >= 12;
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  const mm = String(m).padStart(2, '0');

  if (!isPM) {
    // AM — keep the existing zero-padded "hh:mm AM" form.
    return `${String(h12).padStart(2, '0')}:${mm} AM`;
  }
  // PM — no leading zero on the hour; drop ":00" on the hour.
  return m === 0 ? `${h12} PM` : `${h12}:${mm} PM`;
}
